import { FindAndReplace } from '../types';
import { useCallback, useContext } from 'react';
import BoardsContext from '../../../../../contexts/BoardsContext';
import GoalsContext from '../../../../../contexts/GoalsContext';
import ScorecardsContext from '../../../../../contexts/ScorecardsContext';
import ReportGadgetsContext from '../../../../../contexts/ReportGadgetsContext';
import DashboardGadgetsContext from '../../../../../contexts/DashboardGadgetsContext';
import MetricListsContext from '../../../../../contexts/MetricListsContext';
import CompoundMetricsContext from '../../../../../contexts/CompoundMetricsContext';
import getAffectedChartDefinitions from './getAffectedChartDefinitions';

const useGetEnrichedReceipt = () => {
  const { boards } = useContext(BoardsContext);
  const { goals } = useContext(GoalsContext);
  const { scorecards } = useContext(ScorecardsContext);
  const { allGadgets: reportGadgets } = useContext(ReportGadgetsContext);
  const { allGadgets: gadgets } = useContext(DashboardGadgetsContext);
  const { metricLists } = useContext(MetricListsContext);
  const { allCompoundMetrics } = useContext(CompoundMetricsContext);

  const getEnrichedContent = useCallback(
    (
      affectedContentIds: FindAndReplace.AffectedContentIds,
    ): FindAndReplace.AffectedContent => {
      const affectedBoards = boards
        .filter((board) => affectedContentIds.boards.includes(board.id))
        .map((board) => ({
          id: board.id,
          name: board.name,
        }));

      const affectedGoals = goals
        .filter((goal) => affectedContentIds.goals.includes(goal.id))
        .map((goal) => ({
          id: goal.id,
          name: goal.title,
        }));

      const affectedScorecards = scorecards
        .filter((scorecard) =>
          affectedContentIds.scorecards.includes(scorecard.id),
        )
        .map((scorecard) => ({
          id: scorecard.id,
          name: scorecard.title,
        }));

      const affectedChartDefinitions = getAffectedChartDefinitions({
        reportGadgets,
        chartDefinitionIds: affectedContentIds.chartDefinitions,
      });

      const affectedDashboardGadgets = gadgets
        .filter((g) => affectedContentIds.dashboardGadgets.includes(g.id))
        .map((dg) => ({
          id: dg.id,
          name: dg.chartDef.name,
        }));

      const affectedMetricLists = metricLists
        .filter((ml) => affectedContentIds.dashboardGadgets.includes(ml.id))
        .map((ml) => ({
          id: ml.id,
          name: ml.name,
        }));

      const affectedCompoundMetrics = allCompoundMetrics
        .filter((cm) => affectedContentIds.compoundMetrics?.includes(cm.id))
        .map((cm) => ({
          id: cm.id,
          name: cm.name,
        }));

      return {
        boards: affectedBoards,
        goals: affectedGoals,
        scorecards: affectedScorecards,
        chartDefinitions: affectedChartDefinitions,
        dashboardGadgets: [...affectedDashboardGadgets, ...affectedMetricLists],
        compoundMetrics: affectedCompoundMetrics,
      };
    },
    [
      allCompoundMetrics,
      boards,
      reportGadgets,
      gadgets,
      goals,
      metricLists,
      scorecards,
    ],
  );

  return getEnrichedContent;
};

export default useGetEnrichedReceipt;
